"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RuleEngine = exports.State = void 0;
var Rules_1 = require("./Rules");
var Types_1 = require("./Types");
function isNotNull(input) {
    return input !== null;
}
var State = /** @class */ (function () {
    function State(playlists, presentations, rulesDataStore, isMultitimezoneEnabled) {
        this.playlists = playlists;
        this.presentations = presentations;
        this.rulesDataStore = rulesDataStore;
        this.isMultitimezoneEnabled = isMultitimezoneEnabled;
    }
    State.prototype.playlist = function (id) {
        var playlist = this.playlists[id];
        if (!playlist) {
            console.warn("RuleEngine skipping playlist ".concat(id, ", playlist not found"));
            // If a playlist is not found, assume it is because the user does not
            // have access to it. This can only happen in the Dashboard preview since
            // the device is allowed access to all assigned content.
            return {
                id: id,
                mode: Types_1.PlaylistMode.Sequential,
                items: [],
                noReadAccess: true
            };
        }
        return playlist;
    };
    State.prototype.presentation = function (id) {
        var presentation = this.presentations[id];
        if (!presentation) {
            console.warn("RuleEngine skipping presentation ".concat(id, ", presentation not found"));
            // If a presentation is not found, assume it is because the user does not
            // have access to it. This can only happen in the Dashboard preview since
            // the device is allowed access to all assigned content.
            return {
                id: id,
                applicationName: 'no-read-access',
                noReadAccess: true
            };
        }
        return presentation;
    };
    State.prototype.updatePlaylists = function (playlists) {
        this.playlists = __assign(__assign({}, this.playlists), playlists);
    };
    State.prototype.updatePresentations = function (presentations) {
        this.presentations = __assign(__assign({}, this.presentations), presentations);
    };
    return State;
}());
exports.State = State;
var RuleEngine = /** @class */ (function () {
    function RuleEngine(playlistId, state, globalRule) {
        this.playlistId = playlistId;
        this.state = state;
        this.index = 0;
        this.previewIndex = 0;
        this.globalRule = globalRule;
        this.renderedPlaylistItems = this.renderPlaylist(this.playlist());
        this.overrideItems = this.renderOverrides(this.playlist());
        this.currentOverride = null;
    }
    RuleEngine.prototype.isOverride = function (item) {
        if (!item.playlistId)
            return false;
        var playlist = this.state.playlists[item.playlistId];
        return !!playlist.override;
    };
    RuleEngine.prototype.renderPlaylist = function (playlist) {
        var _this = this;
        return playlist.items
            .map(function (item) {
            if (_this.isOverride(item)) {
                return null;
            }
            if (item.presentationId) {
                return item;
            }
            else if (item.playlistId) {
                return new RuleEngine(item.playlistId, _this.state, _this.globalRule);
            }
            throw new Error("Malformed playlist item: ".concat(JSON.stringify(item)));
        })
            .filter(isNotNull);
    };
    RuleEngine.prototype.renderOverrides = function (playlist) {
        var _this = this;
        return playlist.items
            .map(function (item) {
            if (!_this.isOverride(item)) {
                return null;
            }
            if (item.presentationId) {
                // TODO: Handle override presentations
                return null;
            }
            else if (item.playlistId) {
                return new RuleEngine(item.playlistId, _this.state, _this.globalRule);
            }
            throw new Error("Malformed playlist item: ".concat(JSON.stringify(item)));
        })
            .filter(isNotNull);
    };
    RuleEngine.prototype.nextNoLoop = function () {
        return this.nextValidPresentation(this.index);
    };
    RuleEngine.prototype.nextWithLoop = function () {
        var result = this.nextNoLoop();
        if (result.presentation === null) {
            this.index = 0;
            return this.nextNoLoop();
        }
        return result;
    };
    RuleEngine.prototype.reset = function () {
        this.index = 0;
        // We need to reset the preview index whenever we reset the advance index. This fixes scenarios
        // where we only reset the advance index before another nested playlist should be played and
        // the original preview index is never reset. We still need to track the preview index
        // separately from the advance index to handle scenarios where the preview index needs to
        // be reset before the advance index is reset.
        this.previewIndex = 0;
    };
    RuleEngine.prototype.resetPreview = function () {
        this.previewIndex = 0;
    };
    RuleEngine.prototype.preview = function (index) {
        return this.nextValidPresentation(index, 'preview');
    };
    RuleEngine.prototype.previewNextNoLoop = function () {
        return this.preview(this.previewIndex);
    };
    RuleEngine.prototype.previewNextWithLoop = function () {
        var result = this.previewNextNoLoop();
        // If previewNextNoLoop returns null we've reached the end of the
        // playlist. Cycle back to the first presentation if there is
        // more than one presentation in the playlist. If there is only one
        // presentation in the playlist we don't want to preload the next one.
        // We know the current presentation is the first one if index is 1
        // because we increment in the next() call, which happens before previewNext().
        if (result.presentation === null) {
            this.previewIndex = 0;
            return this.previewNextNoLoop();
        }
        return result;
    };
    // Main function to be called externally from the RuleEngine.
    RuleEngine.prototype.next = function () {
        var _a = this.nextWithLoop(), presentation = _a.presentation, shouldInterrupt = _a.shouldInterrupt, playlistId = _a.playlistId, rule = _a.rule;
        var presentationId = presentation ? presentation.id : null;
        return {
            presentationId: presentationId,
            shouldInterrupt: shouldInterrupt,
            playlistId: playlistId,
            rule: rule
        };
    };
    // Previews the next presentation ID to be played.
    RuleEngine.prototype.previewNext = function () {
        var _a = this.previewNextWithLoop(), presentation = _a.presentation, shouldInterrupt = _a.shouldInterrupt, playlistId = _a.playlistId, rule = _a.rule;
        var presentationId = presentation ? presentation.id : null;
        return {
            presentationId: presentationId,
            shouldInterrupt: shouldInterrupt,
            playlistId: playlistId,
            rule: rule
        };
    };
    RuleEngine.prototype.isPresentationItem = function (value) {
        return value && 'presentationId' in value && !!value.presentationId;
    };
    RuleEngine.prototype.isRuleEngine = function (value) {
        return value && 'playlistId' in value && !!value.playlistId;
    };
    // Returns the currently active override.
    // Returns null if there is no active override.
    RuleEngine.prototype.findActiveOverride = function () {
        // Override priority is determined by playlist items order.
        for (var i = 0; i < this.overrideItems.length; i++) {
            var item = this.overrideItems[i];
            var playlist = this.state.playlist(item.playlistId);
            if (playlist && item.shouldPlay(playlist).shouldPlay) {
                return item;
            }
        }
        // Check nested playlists for an active override.
        for (var i = 0; i < this.renderedPlaylistItems.length; i++) {
            var item = this.renderedPlaylistItems[i];
            if (this.isRuleEngine(item)) {
                var activeOverride = item.findActiveOverride();
                if (activeOverride) {
                    return activeOverride;
                }
            }
        }
        return null;
    };
    // Return the next valid presentation starting at the given index.
    RuleEngine.prototype.nextValidPresentation = function (index, mode) {
        if (mode === void 0) { mode = 'advance'; }
        // Evaluate the main playlist before evaluating any of it's items.
        var evalResult = this.shouldPlay(this.playlist());
        if (!evalResult.shouldPlay) {
            return {
                presentation: null,
                playlistId: this.playlistId,
                rule: evalResult.rule,
                shouldInterrupt: false
            };
        }
        // Check if any overrides are active.
        var item = null;
        var activeOverrideItem = this.findActiveOverride();
        var overrideActive = activeOverrideItem !== null;
        var shouldInterrupt = this.currentOverride !== activeOverrideItem;
        // If this item comes from an override that is different from the last item,
        // and we are in 'advance' mode (i.e. not previewing), update currentOverride.
        if (shouldInterrupt && mode === 'advance') {
            this.currentOverride = activeOverrideItem;
        }
        if (overrideActive && index <= this.renderedPlaylistItems.length) {
            // If override active, use the override item.
            item = activeOverrideItem;
        }
        else if (index < this.renderedPlaylistItems.length) {
            //we don't know why this is <, maybe it could be <= but this may affect previewNext and we dont have good test coverage for previewNext
            // If no overrides are active, use the normal playlist items.
            item = this.renderedPlaylistItems[index];
        }
        // Find the presentation to return.
        if (this.isPresentationItem(item)) {
            // 1. If in 'advance' mode, increment the current index.
            //    This is done to keep track of which index was last valid.
            if (mode === 'advance' && !overrideActive) {
                this.index = index + 1;
                this.previewIndex = index + 1;
            }
            // 2. Check whether the presentation should be played.
            var presentationCandidate = this.state.presentation(item.presentationId);
            var evalResult_1 = this.shouldPlay(presentationCandidate);
            if (!evalResult_1.shouldPlay) {
                // The current presentation should not be played.
                // Find the next valid presentation and update the
                // presentation/rule to be returned.
                var result = this.nextValidPresentation(index + 1, mode);
                return __assign(__assign({}, result), { shouldInterrupt: shouldInterrupt });
            }
            else {
                // This is the "base case", where the current presentation presentatio
                // should be played. Return it.
                return {
                    presentation: presentationCandidate,
                    rule: evalResult_1.rule,
                    shouldInterrupt: shouldInterrupt,
                    playlistId: this.playlistId
                };
            }
        }
        else if (this.isRuleEngine(item)) {
            var playlist = this.state.playlist(item.playlistId);
            // If rule evaluates to false on the playlist item,
            // continue on to the next item.
            var evalResult_2 = this.shouldPlay(playlist);
            if (!evalResult_2.shouldPlay) {
                var result = this.nextValidPresentation(index + 1, mode);
                return __assign(__assign({}, result), { shouldInterrupt: shouldInterrupt });
            }
            else {
                // Otherwise, check if the next item in the playlist is
                // available for playback.
                if (mode === 'advance') {
                    // When override is active, continue to loop over the override
                    // because the override's rule engine will maintain its index state.
                    // If not active, we reset this rule engine's state to restart the loop.
                    var result = overrideActive
                        ? item.nextWithLoop()
                        : item.nextNoLoop();
                    if (!result.presentation && !overrideActive) {
                        // If the playlist is out of items, reset the
                        // playlist playback state so it will start over
                        // on the next loop.
                        item.reset();
                        // Incrementing/Reseting the index for the parent playlist depending upon the length of the items inside it.
                        if (index < this.renderedPlaylistItems.length - 1) {
                            this.index = index + 1;
                        }
                        else {
                            this.reset();
                        }
                        var result_1 = this.nextValidPresentation(index + 1, mode);
                        return this.propagateInterrupt(result_1, shouldInterrupt);
                    }
                    return this.propagateInterrupt(result, shouldInterrupt);
                }
                else {
                    // preview mode
                    var result = overrideActive
                        ? item.previewNextWithLoop()
                        : item.previewNextNoLoop();
                    if (!result.presentation && !overrideActive) {
                        // reseting the index for the item since the last presentation was played and the index should be back to 0
                        item.resetPreview();
                        // Incrementing/Reseting the preview index for the parent playlist depending upon the length of the items inside it.
                        // if the last presentation of the current playlist is already played and there is nothing else to be played
                        // we have to either increment the parent playlist index or reset to 0 depending upon if there is something else remaining to be played or not.//
                        if (index < this.renderedPlaylistItems.length - 1) {
                            this.previewIndex = index + 1;
                        }
                        else {
                            this.resetPreview();
                        }
                        var result_2 = this.nextValidPresentation(index + 1, mode);
                        return this.propagateInterrupt(result_2, shouldInterrupt);
                    }
                    return this.propagateInterrupt(result, shouldInterrupt);
                }
            }
        }
        // If we are out of presentations, return "empty" object.
        return {
            presentation: null,
            rule: null,
            shouldInterrupt: shouldInterrupt,
            playlistId: this.playlistId
        };
    };
    RuleEngine.prototype.propagateInterrupt = function (result, shouldInterrupt) {
        // Bubble up shouldInterrupt if it's true, otherwise use the shouldInterrupt
        // value from result.
        if (shouldInterrupt) {
            return __assign(__assign({}, result), { shouldInterrupt: shouldInterrupt });
        }
        return result;
    };
    // Method to check the items of a playlist should play or not
    RuleEngine.prototype.playlistItemsShouldPlay = function (playlist) {
        var shouldPlayObj = { shouldPlay: false, rule: null };
        for (var i = 0; i < playlist.items.length; i++) {
            var item = playlist.items[i];
            if (item.presentationId) {
                shouldPlayObj = this.shouldPlay(this.state.presentations[item.presentationId]);
            }
            else if (item.playlistId) {
                shouldPlayObj = this.shouldPlay(this.state.playlist(item.playlistId));
            }
            if (shouldPlayObj && shouldPlayObj.shouldPlay === true) {
                return shouldPlayObj;
            }
        }
        return shouldPlayObj;
    };
    RuleEngine.prototype.shouldPlay = function (presentationOrPlaylist) {
        // special case shouldPlay handling for a "non scheduled override: true + rules on items" condition on a playlist and checking the shouldPlay for it's items.
        // only play the playlist if aleast one of its items' rule evaluation comes out to be True.
        // since scheduleRule with override = true is always evaluated at the playlist level even if the rules are applied to the playlist items,
        // hence the check !presentationOrPlaylist.scheduleRule
        if ('override' in presentationOrPlaylist &&
            presentationOrPlaylist.isRuleOnItems &&
            presentationOrPlaylist.override === true &&
            !presentationOrPlaylist.scheduleRule) {
            return this.playlistItemsShouldPlay(presentationOrPlaylist);
        }
        var rule = this.getRule(presentationOrPlaylist);
        var hasRule = !!rule;
        if (presentationOrPlaylist.noReadAccess ||
            presentationOrPlaylist.isDeleted) {
            return { shouldPlay: false, rule: null };
        }
        var shouldPlay = !hasRule ||
            (hasRule &&
                (0, Rules_1.evalRule)(rule, presentationOrPlaylist, this.state.rulesDataStore, this.state.isMultitimezoneEnabled));
        return { shouldPlay: shouldPlay, rule: rule };
    };
    RuleEngine.prototype.playlist = function () {
        // This is implemented as a lookup against state
        // because tags in state may be refreshed during runtime.
        return this.state.playlist(this.playlistId);
    };
    RuleEngine.prototype.getRule = function (presentationOrPlaylist) {
        var rule = null;
        var combineRules = [];
        // checking if parent playlist rules are applied on its presentations
        var parentPlaylist = this.playlist();
        if (parentPlaylist.isRuleOnItems &&
            parentPlaylist.rule &&
            parentPlaylist.id !== presentationOrPlaylist.id) {
            combineRules.push(parentPlaylist.rule);
        }
        // If the playlist has a rule and rules are applied on playlist only, use it's rule to evaluate itself.
        if ('rule' in presentationOrPlaylist &&
            presentationOrPlaylist.rule &&
            !presentationOrPlaylist.isRuleOnItems) {
            combineRules.push(presentationOrPlaylist.rule);
        }
        if (this.globalRule) {
            combineRules.push(this.globalRule);
        }
        // Treating schedule as a separate rule for the playlist in order to avoid skipping the schedule on playlist and passing it on to its children
        // in case of rules on playlist items
        if ('scheduleRule' in presentationOrPlaylist &&
            presentationOrPlaylist.scheduleRule) {
            combineRules.push(presentationOrPlaylist.scheduleRule);
        }
        if (combineRules.length > 1) {
            rule = {
                type: 'and',
                group: combineRules
            };
        }
        else if (combineRules.length == 1) {
            rule = combineRules[0];
        }
        return rule;
    };
    return RuleEngine;
}());
exports.RuleEngine = RuleEngine;
